 <template>
  <div class="modal" :class="{ 'is-active': showModal }">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head p-4" style="box-shadow: none;">
        <p class="modal-card-title">Long Running Timer Detected</p>
        <button class="delete" aria-label="close" @click="dismissModal"></button>
      </header>
      <section class="modal-card-body p-4">
        <p>
          You have an active timer from a previous session that has been running for 
          {{ formatRunningTime(oldActiveTimer?.activeEventStartDate) }}.
        </p>
        <p class="mt-3">
          <strong>Timer:</strong> {{ oldActiveTimer?.title || 'Untitled Timer' }}
          <span v-if="oldActiveTimer?.startDate" class="has-text-grey">
            ({{ formatLocalizedTime(oldActiveTimer.startDate) }}<template v-if="oldActiveTimer?.endDate"> – {{ formatLocalizedTime(oldActiveTimer.endDate) }}</template>)
          </span>
        </p>
        <p v-if="oldActiveTimer?.activeEventStartDate">
          <strong>Started:</strong>
          {{ formatFullDate(oldActiveTimer.activeEventStartDate) }} at {{ formatLocalizedTime(oldActiveTimer.activeEventStartDate) }}
          <span v-if="daysAgoLabel" class="has-text-grey">({{ daysAgoLabel }})</span>
        </p>
      </section>
      <footer class="modal-card-foot p-4">
        <div class="field has-addons">
          <p class="control">
            <button
              class="button is-link"
              :disabled="!oldActiveTimer"
              @click="stopAtFullDuration"
            >
              {{ defaultStopLabel }}
            </button>
          </p>
          <p class="control">
            <TimerControlDropdown
              v-if="oldActiveTimer"
              :timer-id="oldActiveTimer.id"
              action-type="stop"
              omit-immediate
              dropdown-button-only
              :custom-presets="customPresets"
              :custom-time-preset="customTimePreset"
              :custom-min-date="customMinDate"
              :custom-max-date="customMaxDate"
              :timer-scheduled-times="timerScheduledTimesLabel"
              @action-completed="handleActionCompleted"
            />
          </p>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { formatLocalizedTime, formatDuration, calculateBlockDuration, formatFullDate } from '../utils/time';
import TimerControlDropdown from './TimerControlDropdown.vue';
import { toastService } from '../utils/toastService.js';

const store = useBlocksStore();
const dismissed = ref(false);

const oldActiveTimer = computed(() => store.oldActiveTimer);
const now = () => new Date();

const blockDurationSeconds = computed(() => {
  if (!oldActiveTimer.value?.startDate || !oldActiveTimer.value?.endDate) return null;
  return calculateBlockDuration(oldActiveTimer.value);
});

const activeEventStartDate = computed(() => {
  const start = oldActiveTimer.value?.activeEventStartDate;
  return start ? new Date(start) : null;
});

const fullDurationEndTime = computed(() => {
  if (!activeEventStartDate.value || blockDurationSeconds.value == null) return now();
  return new Date(activeEventStartDate.value.getTime() + blockDurationSeconds.value * 1000);
});

const capToNow = (date) => {
  const current = now();
  return date > current ? current : date;
};

const blockEndTime = computed(() => {
  if (!oldActiveTimer.value?.endDate) return null;
  return new Date(oldActiveTimer.value.endDate);
});

const defaultStopTime = computed(() => capToNow(fullDurationEndTime.value));

const formatTimeWithDayOffset = (date) => {
  const timeLabel = formatLocalizedTime(date);

  if (!activeEventStartDate.value) return timeLabel;

  const startDate = new Date(activeEventStartDate.value);
  const endDate = new Date(date);

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return timeLabel;

  const suffix = diffDays === 1 ? '(+1 day)' : `(+${diffDays} days)`;
  return `${timeLabel} ${suffix}`;
};

const defaultStopLabel = computed(() => {
  const endLabel = formatTimeWithDayOffset(defaultStopTime.value);
  if (blockDurationSeconds.value != null && blockDurationSeconds.value > 0) {
    return `Stop at ${formatDuration(blockDurationSeconds.value)} (${endLabel})`;
  }
  return `Stop Now (${endLabel})`;
});

const customPresets = computed(() => {
  if (!oldActiveTimer.value || !activeEventStartDate.value) return [];

  const presets = [];
  const blockEnd = blockEndTime.value ? capToNow(blockEndTime.value) : null;

  if (blockEnd && blockEnd > activeEventStartDate.value) {
    presets.push({
      label: `Stop at timer end — ${formatTimeWithDayOffset(blockEnd)}`,
      endedAt: blockEnd.toISOString()
    });
  }

  presets.push({
    label: `Stop Now — ${formatTimeWithDayOffset(now())}`,
    endedAt: now().toISOString()
  });

  return presets;
});

const customTimePreset = computed(() => {
  if (!activeEventStartDate.value) return now();

  const baseBlockEnd = blockEndTime.value;
  const baseFullDuration = fullDurationEndTime.value;
  let preferred = baseFullDuration;

  if (baseBlockEnd && baseBlockEnd > baseFullDuration) {
    preferred = baseBlockEnd;
  }

  return capToNow(preferred).toISOString();
});

const customMinDate = computed(() => activeEventStartDate.value || null);
const customMaxDate = computed(() => now());

const timerScheduledTimesLabel = computed(() => {
  if (!oldActiveTimer.value?.startDate) return null;
  const start = formatLocalizedTime(oldActiveTimer.value.startDate);
  const end = oldActiveTimer.value?.endDate ? formatLocalizedTime(oldActiveTimer.value.endDate) : null;
  return end ? `${start} – ${end}` : start;
});

const daysAgoLabel = computed(() => {
  if (!activeEventStartDate.value) return '';

  const nowDate = new Date();
  const startDate = new Date(activeEventStartDate.value);
  nowDate.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((nowDate - startDate) / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return '';
  return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
});

// Only show modal if there's an old active timer, session isn't expired, and user hasn't dismissed
const showModal = computed(() => {
  if (dismissed.value) return false;
  if (!oldActiveTimer.value) return false;
  if (!store.userSession?.initializedAt) return false;
  
  // Don't show if session expired modal should be shown (give it priority)
  try {
    if (store.isFrontendOnly) {
      const twentyHoursAgo = new Date(Date.now() - 30 * 60 * 60 * 1000);
      const sessionDate = new Date(store.userSession.initializedAt);
      if (store.userSession?.expired === true || sessionDate < twentyHoursAgo) {
        return false;
      }
    } else {
      const thirtyHoursAgo = new Date(Date.now() - 30 * 60 * 60 * 1000);
      const sessionDate = new Date(store.userSession.initializedAt);
      if (sessionDate < thirtyHoursAgo) {
        return false;
      }
    }
  } catch (error) {
    console.warn('Error checking session dates:', error);
    return false;
  }
  
  return true;
});

const formatRunningTime = (startDate) => {
  if (!startDate) return '0 minutes';
  
  try {
    const now = new Date();
    const start = new Date(startDate);
    
    // Check if date is valid
    if (isNaN(start.getTime())) return '0 minutes';
    
    const diffMs = now.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} and ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    }
  } catch (error) {
    console.warn('Error formatting running time:', error);
    return '0 minutes';
  }
};

const stopAtFullDuration = async () => {
  if (!oldActiveTimer.value) return;

  const result = await store.stopTimer(oldActiveTimer.value.id, {
    endedAt: defaultStopTime.value.toISOString()
  });

  if (result && !result.success) {
    toastService.error(result.message || 'An error occurred');
    return;
  }

  dismissed.value = true;
};

const handleActionCompleted = () => {
  dismissed.value = true;
};

const dismissModal = () => {
  dismissed.value = true;
};
</script>

<style scoped>
.modal-card,
.modal-card-foot,
.modal-card-body {
  overflow: visible;
}
</style>
